import { User } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";
import { db } from "../lib/firebase";
import { isCacheOnlyMissingDocument } from "../lib/realtimeRecovery";
import type { UserProfile } from "../types/models";

export type ProfileLoadError = {
  code: string;
  message: string;
  offline: boolean;
};

function toProfileLoadError(error: unknown): ProfileLoadError {
  const value = error as { code?: unknown; message?: unknown } | null;
  const code = typeof value?.code === "string" ? value.code : "unknown";
  const message =
    typeof value?.message === "string"
      ? value.message
      : "Impossible de charger le profil.";
  const normalized = `${code} ${message}`.toLowerCase();
  const offline =
    code === "unavailable" ||
    code === "deadline-exceeded" ||
    normalized.includes("network") ||
    normalized.includes("offline");

  return { code, message, offline };
}

export function useProfile(user: User | null) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileUid, setProfileUid] = useState<string | null>(null);
  const [error, setError] = useState<ProfileLoadError | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const userUid = user?.uid ?? null;

  useEffect(() => {
    if (!userUid) {
      setProfile(null);
      setProfileUid(null);
      setError(null);
      setLoadingProfile(false);
      return;
    }

    let active = true;
    const uid = userUid;

    // Do not expose the previous user's profile while the new subscription starts.
    setProfile(null);
    setProfileUid(null);
    setError(null);
    setLoadingProfile(true);
    const ref = doc(db, "users", uid);

    const unsub = onSnapshot(
      ref,
      { includeMetadataChanges: true },
      (snap) => {
        if (!active) return;
        if (isCacheOnlyMissingDocument(snap.exists(), snap.metadata.fromCache)) {
          setProfile(null);
          setProfileUid(uid);
          setError({
            code: "unavailable",
            message: "Profil indisponible hors ligne.",
            offline: true,
          });
          setLoadingProfile(false);
          return;
        }
        setProfile(snap.exists() ? (snap.data() as UserProfile) : null);
        setProfileUid(uid);
        setError(null);
        setLoadingProfile(false);
      },
      (snapshotError) => {
        if (!active) return;
        // A read error is not evidence that the profile does not exist.
        setProfileUid(uid);
        setError(toProfileLoadError(snapshotError));
        setLoadingProfile(false);
      }
    );

    return () => {
      active = false;
      unsub();
    };
  }, [retryKey, userUid]);

  const retry = useCallback(() => {
    setRetryKey((key) => key + 1);
  }, []);

  const hasCurrentUserProfile = !!userUid && profileUid === userUid;

  return {
    profile: hasCurrentUserProfile ? profile : null,
    loadingProfile: !!userUid && (!hasCurrentUserProfile ? true : loadingProfile),
    error: hasCurrentUserProfile ? error : null,
    retry,
  };
}
